import { normalizeName } from '../shared/naming';

/**
 * As etapas da obra — preliminar, fundação, contenção e estrutura — em disciplinas e
 * serviços (Fase 23).
 *
 * Transcritas de `Gilvan - Etapas.xlsx` em 25/09/2026. A planilha tem três colunas, Local →
 * Disciplina → Serviço, e o **Local não entra aqui**: ele é pavimento (ADR-141). Preliminar,
 * Fundação e Contenção são andares abaixo do térreo, "Estrutura" são os andares de verdade,
 * e a frente continua sendo pavimento × disciplina. O que a planilha chama de disciplina é
 * a **peça** — pilar, viga, laje —, e é ela que permite dizer "a laje do 3º acabou".
 *
 * ## A grafia é corrigida, ao contrário da planilha do cliente
 *
 * `SERVICE_REGISTRY` guarda até o erro de digitação da construtora, porque é o nome da obra
 * que manda numa conversa sobre serviço. Esta planilha é do próprio usuário, e ele pediu o
 * contrário: maiúsculas, acento e erro corrigidos, e o par com hífen virando frase —
 * "Forma - Montagem" é `MONTAGEM DE FÔRMA`.
 *
 * ## Disciplina repetida entre locais é uma só
 *
 * BLOCO e VIGAS BALDRAME aparecem na Fundação e na Contenção. Como o local é o pavimento, as
 * duas frentes — `BLOCO — Fundação` e `BLOCO — Contenção` — já se distinguem, e a disciplina
 * leva a soma dos serviços (a lista da Contenção cabe inteira na da Fundação). A exceção é o
 * pilar: o da contenção nasceu perfurando e rompendo estaca, e somá-lo ao da estrutura poria
 * "PERFURAÇÃO" na lista do 3º pavimento — daí `PILARES DE CONTENÇÃO`. Desde a Fase 27 (F6) ele
 * é moldado com fôrma, como o da estrutura, e continua separado: a peça dele é outra (a
 * contenção), e o muro e a cortina são só dele.
 *
 * A unidade é explícita, e não palpitada pelo nome como no resto do catálogo: ferragem é
 * **kg**, e nenhuma medida da geometria leva a quilo.
 */

export type Stage = 'PRELIMINAR' | 'FUNDACAO' | 'CONTENCAO' | 'ESTRUTURA';

export interface StageService {
  readonly name: string;
  /** Código em `DEFAULT_UNITS`. */
  readonly unitCode: string;
}

export interface StageDiscipline {
  readonly stage: Stage;
  readonly name: string;
  readonly services: readonly StageService[];
  /**
   * A peça que o desenho do quantitativo conhece, quando a disciplina é uma delas (Fase 24).
   * Ausente = nenhuma peça na planta ainda: os serviços nascem digitados (o ARRANQUE DO
   * PILAR, até a etapa 2 da Fase 28; a preliminar e a LOCAÇÃO DA OBRA, sempre).
   *
   * PILARES e VIGAS, desde a etapa 1b da Fase 27 as ESTACAS, desde a etapa 2 o BLOCO e as VIGAS
   * BALDRAME e desde a etapa 3 a LAJE DE FUNDAÇÃO (o radier) e a LAJE: são as que a planta
   * traça. É o que deixa a CONCRETAGEM delas medir o volume **da própria peça** (RN-259) — a
   * disciplina é a peça (ADR-141), e o nome do serviço, CONCRETAGEM, não diz qual. Desde a etapa 4
   * a PILARES DE CONTENÇÃO é o `PILAR_CONTENCAO`, peça própria (F2): medi-lo como PILAR somaria o
   * da contenção ao da estrutura. O MURO DE CONTENÇÃO e a CORTINA ATIRANTADA, serviços dela,
   * medem o `MURO_CONTENCAO` (F7) — quem decide é o nome do serviço, em `default-services.ts`.
   *
   * A estaca, o bloco, o baldrame, o radier, a laje e as de contenção são peças próprias
   * (ADR-175), e não ambiente nem pilar: o "Mede por" de cada serviço deles é decidido pelo nome
   * (`default-services.ts`), porque os de "un" medem coisas diferentes e os de "m3" não são todos
   * o volume (o lastro mede a área, e o reaterro do radier e a movimentação de terra ficam
   * digitados).
   */
  readonly element?:
    | 'PILAR'
    | 'VIGA'
    | 'ESTACA'
    | 'BLOCO'
    | 'BALDRAME'
    | 'RADIER'
    | 'LAJE'
    | 'PILAR_CONTENCAO'
    | 'MURO_CONTENCAO'
    | 'ESCADA';
}

/**
 * A cor é a da etapa, e não uma por disciplina.
 *
 * As dezessete entram lado a lado na árvore; com uma cor por etapa, lê-se de relance onde
 * a fundação termina e a estrutura começa. Continua sendo apoio — o nome está sempre escrito.
 */
export const STAGE_COLOR: Readonly<Record<Stage, string>> = {
  PRELIMINAR: '#a8763f',
  FUNDACAO: '#8b6f47',
  CONTENCAO: '#5a8fa8',
  ESTRUTURA: '#6b7280',
};

const un = (name: string): StageService => ({ name, unitCode: 'un' });
const m = (name: string): StageService => ({ name, unitCode: 'm' });
const m2 = (name: string): StageService => ({ name, unitCode: 'm2' });
const m3 = (name: string): StageService => ({ name, unitCode: 'm3' });
const kg = (name: string): StageService => ({ name, unitCode: 'kg' });
const vb = (name: string): StageService => ({ name, unitCode: 'vb' });

const FORMA = [
  m2('CONFECÇÃO DE FÔRMA'),
  m2('MONTAGEM DE FÔRMA'),
  m2('DESMONTAGEM DE FÔRMA'),
] as const;
/**
 * A ferragem em três tempos, na ordem da obra: cortar e dobrar a barra, montar a armadura,
 * instalá-la na fôrma (Fase 27, D13).
 *
 * Os três contam o mesmo kg, mas só o CORTE E DOBRA **compra** aço (RN-366): a planilha que
 * punha os 31,56 kg na montagem e na instalação comprava o aço duas vezes.
 */
const FERRAGEM = [
  kg('CORTE E DOBRA DE FERRAGEM'),
  kg('MONTAGEM DE FERRAGEM'),
  kg('INSTALAÇÃO DE FERRAGEM'),
] as const;
const CONCRETAGEM = m3('CONCRETAGEM');
const CONTROLE = un('CONTROLE TECNOLÓGICO DO CONCRETO');
const MOVIMENTACAO = m3('MOVIMENTAÇÃO DE TERRA');
const COMPACTACAO = m2('COMPACTAÇÃO');
const IMPERMEABILIZACAO = m2('IMPERMEABILIZAÇÃO');
const ESCORAMENTO = m2('ESCORAMENTO');
const PERFURACAO = m('PERFURAÇÃO');
/**
 * Era ROMPIMENTO DE ESTACA (Fase 27, D14, RN-377): arrasar é o nome da obra para cortar a
 * cabeça da estaca na cota do bloco. A `0053` renomeia a linha, com o mesmo id.
 */
const ARRASAMENTO = un('ARRASAMENTO DE ESTACA');

/**
 * As dezesseis da planilha, na ordem dela — que é a de execução, e vira `position` —, e o
 * ARRANQUE DO PILAR, que a planilha por peça trouxe na Fase 28 (a `0064`).
 *
 * Em itálico no plano, e marcados aqui, os serviços que **mudaram de casa**: vinham do
 * acréscimo da planilha de inspeções (Fase 21) em ESTRUTURAL e CONTENÇÃO, e a planilha de
 * etapas não os traz. Contados em unidade, como já estavam.
 */
export const STAGE_REGISTRY: readonly StageDiscipline[] = [
  { stage: 'PRELIMINAR', name: 'TOPOGRAFIA', services: [un('TOPOGRAFIA')] },
  { stage: 'PRELIMINAR', name: 'LAUDO DE SONDAGEM', services: [un('LAUDO DE SONDAGEM')] },
  {
    stage: 'PRELIMINAR',
    name: 'LIGAÇÕES PROVISÓRIAS',
    services: [un('ÁGUA'), un('LUZ'), un('ESGOTO')],
  },
  // As três sem serviço na planilha ganham um de mesmo nome: sem serviço, não se planeja
  // nem se mede (RN-230, RN-232).
  { stage: 'PRELIMINAR', name: 'LIMPEZA DO LOTE', services: [m2('LIMPEZA DO LOTE')] },
  { stage: 'PRELIMINAR', name: 'TERRAPLANAGEM', services: [m3('TERRAPLANAGEM')] },
  {
    stage: 'PRELIMINAR',
    name: 'CANTEIRO DE OBRA',
    services: [
      vb('CANTEIRO DE OBRA'),
      // Mudou de casa: era da ESTRUTURAL. A betoneira e a central de argamassa são do
      // canteiro, e a ficha 27 — traço, dosagem, betoneira limpa — vai junto.
      m3('PRODUÇÃO DE ARGAMASSA E CONCRETO'),
    ],
  },
  { stage: 'FUNDACAO', name: 'LOCAÇÃO DA OBRA', services: [un('TOPOGRAFIA'), m('GABARITO')] },
  {
    stage: 'FUNDACAO',
    name: 'ESTACAS',
    // A ordem de execução (usuário, 16/10/2026), que o cronograma gerado do quantitativo segue
    // (Fase 31, D4): a armação se corta e se monta antes; o furo é aberto e concretado; o controle
    // tecnológico acompanha a concretagem; e a armação é instalada no concreto fresco. A perfuração
    // nunca vem depois da concretagem, nem a instalação antes dela.
    services: [
      FERRAGEM[0],
      FERRAGEM[1],
      PERFURACAO,
      CONCRETAGEM,
      CONTROLE,
      FERRAGEM[2],
      MOVIMENTACAO,
      ARRASAMENTO,
      un('TESTE DE RESISTÊNCIA DA ESTACA'),
    ],
    element: 'ESTACA',
  },
  {
    stage: 'FUNDACAO',
    name: 'BLOCO',
    services: [
      MOVIMENTACAO,
      COMPACTACAO,
      m3('LASTRO DE CONCRETO'),
      ...FORMA,
      un('LIMPEZA DO FUNDO DO BLOCO'),
      ...FERRAGEM,
      CONCRETAGEM,
      IMPERMEABILIZACAO,
      // A planilha por peça o traz, e o catálogo não o tinha (Fase 28, RN-391): a terra que
      // volta em volta do bloco pronto e impermeabilizado. Digitado, como o do radier — o
      // desenho não sabe a vala. A `0064` o cria na base que já existia.
      m3('REATERRO'),
      CONTROLE,
    ],
    element: 'BLOCO',
  },
  {
    // A espera do pilar, amarrada na fôrma do bloco antes da concretagem — por isso logo
    // depois dele (Fase 28, D2, RN-391). A planilha por peça a escreve como peça própria
    // (AP24 ao lado do B24), e a disciplina é a peça (ADR-141). Só a ferragem, digitada: a
    // peça na planta chega na etapa 2 da Fase 28 (D4), e até lá o kg vem pronto da planilha.
    stage: 'FUNDACAO',
    name: 'ARRANQUE DO PILAR',
    services: [...FERRAGEM],
  },
  {
    stage: 'FUNDACAO',
    name: 'VIGAS BALDRAME',
    services: [
      MOVIMENTACAO,
      COMPACTACAO,
      m3('LASTRO DE CONCRETO'),
      ...FORMA,
      ...FERRAGEM,
      CONCRETAGEM,
      IMPERMEABILIZACAO,
      CONTROLE,
    ],
    element: 'BALDRAME',
  },
  {
    stage: 'FUNDACAO',
    name: 'LAJE DE FUNDAÇÃO',
    services: [
      m3('REATERRO'),
      m2('REGULARIZAÇÃO DO TERRENO'),
      COMPACTACAO,
      IMPERMEABILIZACAO,
      // O radier não tinha fôrma no cadastro (F9): a borda — perímetro × espessura — é medida
      // mesmo assim, e a de montagem é a que a obra mede. Entra antes da ferragem, na ordem de
      // execução.
      m2('MONTAGEM DE FÔRMA'),
      ...FERRAGEM,
      CONCRETAGEM,
      CONTROLE,
    ],
    element: 'RADIER',
  },
  {
    stage: 'CONTENCAO',
    name: 'PILARES DE CONTENÇÃO',
    // Moldado com fôrma (Fase 27, F6, decidido em 05/10/2026): entram os três serviços de
    // FÔRMA, e a PERFURAÇÃO e o ROMPIMENTO DE ESTACA saem — desativados na `0053`, e não
    // apagados, porque o que já foi medido neles continua contando o que aconteceu.
    services: [
      ...FORMA,
      ...FERRAGEM,
      CONCRETAGEM,
      MOVIMENTACAO,
      CONTROLE,
      // Mudaram de casa: eram da CONTENÇÃO. Passam de "un" a "m2" (F7): a face do muro, em
      // elevação, é o que a planta mede; os tirantes continuam digitados (D9).
      m2('CORTINA ATIRANTADA'),
      m2('MURO DE CONTENÇÃO'),
    ],
    element: 'PILAR_CONTENCAO',
  },
  {
    stage: 'ESTRUTURA',
    name: 'PILARES',
    services: [...FORMA, ...FERRAGEM, CONCRETAGEM, CONTROLE],
    element: 'PILAR',
  },
  {
    stage: 'ESTRUTURA',
    name: 'VIGAS',
    services: [...FORMA, ...FERRAGEM, ESCORAMENTO, CONCRETAGEM, CONTROLE],
    element: 'VIGA',
  },
  {
    stage: 'ESTRUTURA',
    name: 'LAJE',
    services: [
      ...FORMA,
      ...FERRAGEM,
      CONCRETAGEM,
      ESCORAMENTO,
      un('INSTALAÇÕES ELÉTRICAS'),
      CONTROLE,
      // Mudaram de casa: eram da ESTRUTURAL.
      un('LAJE PRÉ-MOLDADA'),
      un('ESTRUTURA PROTENDIDA'),
    ],
    element: 'LAJE',
  },
  {
    stage: 'ESTRUTURA',
    name: 'ESCADAS',
    services: [
      ...FORMA,
      ...FERRAGEM,
      CONCRETAGEM,
      ESCORAMENTO,
      CONTROLE,
      // Mudaram de casa: eram da ESTRUTURAL.
      un('FABRICAÇÃO DE PRÉ-MOLDADOS'),
      un('MONTAGEM DE ESCADA'),
    ],
    element: 'ESCADA',
  },
];

/** As disciplinas de uma etapa, na ordem do registro. */
export function stageDisciplineNames(stage: Stage): readonly string[] {
  return STAGE_REGISTRY.filter((disciplina) => disciplina.stage === stage).map(
    (disciplina) => disciplina.name,
  );
}

/** Os serviços que o registro de etapas conhece para esta disciplina. */
export function stageServicesOf(disciplineName: string): readonly string[] {
  const chave = normalizeName(disciplineName);
  return (
    STAGE_REGISTRY.find((disciplina) => normalizeName(disciplina.name) === chave)?.services.map(
      (servico) => servico.name,
    ) ?? []
  );
}

// ── A virada (ADR-142) ──────────────────────────────────────────────────────────

/** As três que dão lugar às peças. Saem desativadas, não apagadas. */
export const RETIRED_DISCIPLINES: readonly string[] = ['ESTRUTURAL', 'FUNDAÇÃO', 'CONTENÇÃO'];

/** Onde havia `ESTRUTURAL — X`, nascem estas quatro em X. */
export const STRUCTURAL_SPLIT: readonly string[] = stageDisciplineNames('ESTRUTURA');

/**
 * O que quem executava FUNDAÇÃO passa a executar: a fundação e a contenção inteiras.
 *
 * A contenção não tinha função ligada — nasceu na Fase 21 sem vínculo —, e o empreiteiro
 * que a executa é o mesmo ofício de quem faz estaca e bloco.
 */
export const FOUNDATION_SPLIT: readonly string[] = [
  ...stageDisciplineNames('FUNDACAO'),
  ...stageDisciplineNames('CONTENCAO'),
];
